import {HttpBackend, HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {CookieService} from 'ngx-cookie-service';
import {LoaderService} from './loader.service';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {finalize, shareReplay} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private userNameListeners = new Subject<any>();
    private user: any = null;
    private readonly httpNoIntercept: HttpClient;
    private refreshInFlight: Observable<any> | null = null;

    constructor(
        private httpService: HttpClient,
        private cookieService: CookieService,
        private loaderService: LoaderService,
        httpBackend: HttpBackend,
    ) {
        this.user = JSON.parse(sessionStorage.getItem('userObject') || 'null');
        this.httpNoIntercept = new HttpClient(httpBackend);
    }

    clearUser(): void {
        this.user = null;
    }

    // The platform rotates the refresh token on each refresh, so concurrent
    // callers must share one request or the second one is rejected.
    refreshSession(): Observable<any> {
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }

        const refreshToken = this.getSessionRefreshToken();

        this.refreshInFlight = this.httpNoIntercept
            .post(
                `${environment.platformUrls.api}/sessions/refresh`,
                {},
                {
                    withCredentials: true,
                    headers: {Authorization: `Bearer ${refreshToken}`},
                },
            )
            .pipe(
                finalize(() => (this.refreshInFlight = null)),
                shareReplay(1),
            );

        return this.refreshInFlight;
    }

    async fetchUser(): Promise<any> {
        let userData = null;
        let userObject = null;

        const cookieExists = this.cookieService.check('buildName');

        if (cookieExists) {
            sessionStorage.setItem('buildName', this.cookieService.get('buildName'));
        }

        const refreshToken = this.getSessionRefreshToken();

        const promise = this.httpService.get(
            `${environment.enterpriseUrls.apiEndpoint}/users/current`,
            {
                params: {
                    include: 'mainMenus,profileMenus',
                    'kebab-case-icon-names': 'true',
                },
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
            },
        );

        try {
            const data = (await lastValueFrom(promise)) as {
                data: any;
                meta?: { platform: { url: string }; featureFlags?: any };
            };

            const enterpriseDashboardId = data?.data?.enterpriseDashboardId || 0;
            data.data.enterpriseDashboardId = enterpriseDashboardId;
            data.data.savedEnterpriseDashboardId = enterpriseDashboardId;

            userData = data.data;
            userObject = JSON.stringify(userData);

            this.setUser(userData);

            const platformUrl = data.meta?.platform?.url || '';
            const featureFlags =
                data.meta?.featureFlags || environment.disableUI.featureFlags;

            sessionStorage.setItem('platformUrl', platformUrl);
            sessionStorage.setItem('userObject', userObject);
            sessionStorage.setItem('featureFlags', JSON.stringify(featureFlags));

            this.userNameListeners.next(true);

            return userData;
        } catch (error: any) {
            this.loaderService.isLoading.next(false);

            if (!(error.status === 401 || error.status === 403)) {
                return this.user;
            }

            const errorMessage = error.error?.message || '';

            if (!errorMessage.includes('Session expired')) {
                return this.user;
            }

            this.logout();

            return this.user;
        }
    }

    fetchUserLight(): any {
        const refreshToken = this.getSessionRefreshToken();

        return this.httpService.get(
            `${environment.enterpriseUrls.apiEndpoint}/users/current`,
            {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
            },
        );
    }

    getUser(): any {
        return this.user;
    }

    getSessionData(datumKey: string): any {
        return this.user?.session?.[datumKey] || undefined;
    }

    getSessionAccessToken(): string {
        const key = this.getSessionData('access_token');

        return this.cookieService.get(key) || '';
    }

    getSessionExpiry(): number {
        const key = this.getSessionData('session_expiry');

        return Number(this.cookieService.get(key));
    }

    getSessionRefreshToken(): string {
        const key = this.getSessionData('refresh_token');

        return this.cookieService.get(key) || '';
    }

    isLoggedIn(): boolean {
        return this.user !== null;
    }

    listen(): Observable<any> {
        return this.userNameListeners.asObservable();
    }

    logout(): void {
        const next = () => {
            this.loaderService.hide();

            let platformUrl = environment.login.externalogoutPage;
            const buildName =
                sessionStorage.getItem('buildName') ??
                this.cookieService.get('buildName');
            platformUrl = platformUrl.replace('{buildName}', buildName);

            sessionStorage.clear();

            if (!buildName) {
                window.location.href = `${environment.login.externaloginPage}/login`;
                return;
            }

            this.clearUser();

            window.location.href = platformUrl;
        };

        const refreshToken = this.getSessionRefreshToken();

        this.httpService
        .post(
            `${environment.enterpriseUrls.apiEndpoint}/logout`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
            },
        )
        .subscribe({
            next,
            error: next,
        });
    }

    setUser(user: any): void {
        this.user = user;
    }
}
